'use client';

import { ChevronDown } from 'lucide-react';

import Link from 'next/link';

import { LineDashedHorizontalIcon } from '@/components/icons/svgs';

import { MUTED_LINK_CLASS } from '../../constants/link-class';
import type { FaqEntry } from '../../constants/landing-constants';

type FAQItemProps = FaqEntry & {
	isLast?: boolean;
};

/**
 * Renders the answer text, turning the first occurrence of `link.match` into an
 * inline link. The full answer stays available as a plain string for schema.
 */
const renderAnswer = (answer: string, link?: FAQItemProps['link']) => {
	if (!link) return answer;

	const index = answer.indexOf(link.match);
	if (index === -1) return answer;

	return (
		<>
			{answer.slice(0, index)}
			<Link
				href={link.href}
				className={
					link.accent
						? 'text-primary-blue no-underline'
						: MUTED_LINK_CLASS
				}
			>
				{link.match}
			</Link>
			{answer.slice(index + link.match.length)}
		</>
	);
};

export const FAQItem = ({ question, answer, link, isLast, isStatic }: FAQItemProps) => {
	const body = (
		<p className="pb-3 text-base leading-relaxed text-muted-foreground md:pb-4 md:text-lg">
			{renderAnswer(answer, link)}
		</p>
	);

	return (
		<div className="border-dashed border-secondary/30">
			{isStatic ? (
				<p className="pt-10 text-base leading-relaxed text-muted-foreground md:pt-14 md:text-lg">
					{question} {renderAnswer(answer, link)}
				</p>
			) : (
				<details className="group">
					<summary className="flex w-full cursor-pointer list-none items-start justify-between gap-3 py-4 text-left marker:hidden focus-visible:outline-none md:py-5 [&::-webkit-details-marker]:hidden">
						<h3 className="text-base font-medium text-primary md:text-xl">{question}</h3>
						<ChevronDown
							strokeWidth={1.5}
							className="size-4 shrink-0 text-secondary transition-transform duration-200 group-open:rotate-180 md:size-5"
						/>
					</summary>
					{body}
				</details>
			)}
			{!isLast && (
				<div className="w-full overflow-hidden">
					<LineDashedHorizontalIcon />
				</div>
			)}
		</div>
	);
};
